import classnames from 'classnames';

import type {PaddingLevel} from './paddingLevelProp';
import {paddingLevels} from './paddingLevelProp';

export type CardBackgroundColor = 'subtle' | 'primary' | 'bold' | 'inherit';
export type CardBorder = 'none' | 'regular' | 'darker';
export type CardRoundness = 'small' | 'medium' | 'large' | 'pill' | 'none';
export type CardShadow = 'closest' | 'close' | 'default' | 'far' | 'farthest';

const BORDER_COLOR: Record<string, string> = {
  regular: 'border-border-primary',
  darker: 'border-border-bold'
};

const ROUNDNESS: Record<string, string> = {
  none: 'rounded-none',
  small: 'rounded-x1',
  medium: 'rounded-x2',
  large: 'rounded-x3',
  pill: 'rounded-[1000px]'
};

const SHADOW: Record<string, string> = {
  closest: 'shadow-l1',
  close: 'shadow-l2',
  default: 'shadow-l3',
  far: 'shadow-l4',
  farthest: 'shadow-l5'
};

export interface CardClassNameOptions {
  backgroundColor?: CardBackgroundColor;
  border?: CardBorder;
  roundness?: CardRoundness;
  shadow?: CardShadow;
  paddingLevel?: PaddingLevel | null;
}

/**
 * Tailwind class string equivalent to the deprecated `<Card>` atomic component's styling.
 * Mirrors the defaults and class output of Card.react.tsx / card.scss exactly so container
 * styling can move onto plain elements + utilities without any visual change (issue #15677).
 * Retained for the few atomic primitives that expose Card's style props as runtime-configurable
 * options; leaf usages inline the equivalent utilities directly.
 */
export function getCardClassName({
  backgroundColor = 'primary',
  border = 'regular',
  roundness = 'small',
  shadow,
  paddingLevel = 0
}: CardClassNameOptions = {}): string {
  return classnames(
    backgroundColor !== 'inherit' && `background-${backgroundColor}`,
    border !== 'none' && ['border', 'border-solid', BORDER_COLOR[border]],
    ROUNDNESS[roundness],
    shadow && SHADOW[shadow],
    paddingLevel != null && paddingLevels[paddingLevel]
  );
}
